import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { retry } from 'rxjs/operators';

import { GetglobalsettingsService } from './getglobalsettings.service';
import {
  AgentRequestWithLines,
  AgentCustomerLookup,
  AgentProductCandidate,
  AgentConfirmOutcome,
  GenericContactSignals,
} from '../shared/agent-data';

/**
 * REST client for API-12 (Agent Data API), `/api/agent/*` on the webapi backend — the
 * day-to-day intake workflow: load a claimed request, look up its customer, resolve
 * catalog candidates, and confirm/abandon. Used by the `create-new-document` dialog's
 * "AI Assist" tab (`new-document-dlg-ai-assist`) — see
 * `docs/PROMPT_agent-intake-confirmation-panel.md`.
 *
 * Errors are NOT caught/alerted here — callers subscribe with their own `error` handler,
 * same convention as `agent-admin-api.service.ts`.
 */
@Injectable({
  providedIn: 'root'
})
export class AgentDataApiService {

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  /** Builds an absolute API URL by prepending the runtime API server address. */
  private _getApiUrl(url: string = ''): string {
    return this.globalsettingsservice.getApiServerUrl() + url;
  }

  /** Fresh standard headers per call — same reasoning as `agent-admin-api.service.ts`. */
  private _options() {
    return { headers: this.globalsettingsservice.getStandardApiHeaders() };
  }

  /** `GET /api/agent/requests/:id` — the request row plus its (currently always-empty,
   *  see `AgentRequestLine`'s doc comment) lines. */
  getRequest(id: number): Observable<AgentRequestWithLines> {
    return this.http.get<AgentRequestWithLines>(this._getApiUrl('/api/agent/requests/' + id), this._options())
      .pipe(retry(1));
  }

  /** `GET /api/agent/customer/:id` — `:id` is a `Clients.CLIID`. */
  getCustomer(cliId: number): Observable<AgentCustomerLookup> {
    return this.http.get<AgentCustomerLookup>(this._getApiUrl('/api/agent/customer/' + cliId), this._options())
      .pipe(retry(1));
  }

  /** `POST /api/agent/resolve` — catalog search with a confidence score. Not retried:
   *  called once per extracted line item on tab load, a retry-on-every-line storm isn't
   *  worth it for what's a manual-review aid, not a critical write. */
  resolveProduct(query: string): Observable<{ candidates: AgentProductCandidate[] }> {
    return this.http.post<{ candidates: AgentProductCandidate[] }>(
      this._getApiUrl('/api/agent/resolve'), { query }, this._options());
  }

  /** `PATCH /api/agent/requests/:id` — confirm (`documentId` should be supplied once the
   *  dialog's own save flow created the document, though the server falls back to its
   *  `draft_document_id` column if omitted) or abandon (`rejectionReason` required).
   *  See `routes/AgentData.ts` for the full contract. */
  confirmRequest(
    id: number,
    outcome: AgentConfirmOutcome,
    opts: { documentId?: number; rejectionReason?: string } = {}
  ): Observable<{ id: number; outcome: AgentConfirmOutcome; status: string; documentId: number | null }> {
    return this.http.patch<{ id: number; outcome: AgentConfirmOutcome; status: string; documentId: number | null }>(
      this._getApiUrl('/api/agent/requests/' + id),
      { outcome, ...opts },
      this._options());
  }

  /** `POST /api/agent/extract-identity` — AG-25, extract company/contact info from pasted text.
   *  Not retried: a parse failure is informative, not worth retrying. */
  extractIdentityFromText(text: string): Observable<GenericContactSignals> {
    return this.http.post<GenericContactSignals>(
      this._getApiUrl('/api/agent/extract-identity'), { text }, this._options());
  }
}
